import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Smartphone, BellRing, BellOff, Crosshair, Send } from "lucide-react";
import { isPushSupported, subscribeToPush, unsubscribeFromPush, sendLivePosition, getLocalSubscription } from "../lib/push";
import { VEHICLE_LABEL } from "../lib/pricing";

const GRID_ORDER = ["car", "van", "truck", "heavy_truck"];

export default function PushNotificationsCard({ vehicleType }) {
  const [supported] = useState(isPushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [grid, setGrid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gpsOk, setGpsOk] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get("/push/radius-grid").then((r) => setGrid(r.data.grid)).catch(() => {});
    if (supported) {
      getLocalSubscription().then((sub) => setSubscribed(Boolean(sub))).catch(() => {});
    }
  }, [supported]);

  useEffect(() => {
    if (!subscribed) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    const push = async () => setGpsOk(await sendLivePosition());
    push();
    intervalRef.current = setInterval(push, 5 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [subscribed]);

  const toggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success("Notifications push désactivées");
      } else {
        await subscribeToPush();
        setSubscribed(true);
        toast.success("🔔 Notifications push activées sur cet appareil");
      }
    } catch (e) {
      const msg = String(e?.message || "");
      if (/permission|denied|refus/i.test(msg)) {
        toast.error("Notifications bloquées — autorisez-les dans les paramètres de votre navigateur pour ce site");
      } else {
        toast.error(formatApiError(e) || "Impossible d'activer les notifications sur cet appareil");
      }
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Notification de test envoyée — vérifiez votre appareil");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-3 border border-[#1F4032]/15 bg-white rounded-md p-5" data-testid="push-notifications-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-[260px]">
          <Smartphone className="w-5 h-5 text-[#1F4032] mt-0.5" />
          <div>
            <div className="font-display font-bold text-lg tracking-tight">
              Notifications push « mission proche »
            </div>
            <div className="text-sm text-neutral-600 mt-1 max-w-md">
              {supported
                ? (subscribed
                  ? "Actives sur cet appareil — vous êtes alerté en temps réel dès qu'une mission apparaît dans votre périmètre, mesuré depuis votre position GPS."
                  : "Recevez une alerte instantanée sur votre téléphone ou ordinateur, même quand l'app est fermée.")
                : "Votre navigateur ne supporte pas les notifications push. Installez l'app (PWA) ou utilisez Chrome/Edge/Firefox."}
            </div>
            {subscribed && gpsOk !== null && (
              <div className={`flex items-center gap-1.5 text-xs mt-2 ${gpsOk ? "text-emerald-700" : "text-amber-600"}`} data-testid="gps-status">
                <Crosshair className="w-3.5 h-3.5" />
                {gpsOk
                  ? "Position GPS temps réel partagée (mise à jour toutes les 5 min)"
                  : "GPS indisponible — proximité mesurée depuis votre ville de base"}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <Button
            onClick={toggle}
            disabled={!supported || busy}
            className={`rounded-md gap-2 ${subscribed ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#1F4032] hover:bg-[#2A5443] text-white"}`}
            data-testid="toggle-push-btn"
          >
            {subscribed ? <BellOff className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
            {busy ? "..." : (subscribed ? "Désactiver" : "Activer les push")}
          </Button>
          {subscribed && (
            <Button variant="outline" size="sm" onClick={sendTest} className="rounded-md gap-1.5 border-[#1F4032]/30" data-testid="push-test-btn">
              <Send className="w-3.5 h-3.5" /> Tester
            </Button>
          )}
        </div>
      </div>

      {grid && (
        <div className="mt-4 pt-4 border-t border-[#1F4032]/10">
          <div className="overline text-neutral-500 mb-2">Périmètre de notification selon votre véhicule</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="push-radius-grid">
            {GRID_ORDER.map((vt) => (
              <div
                key={vt}
                className={`border rounded-md p-3 text-center ${vt === vehicleType ? "border-[#1F4032] bg-[#1F4032] text-white" : "border-[#1F4032]/15 bg-neutral-50"}`}
                data-testid={`grid-cell-${vt}`}
              >
                <div className={`text-xs ${vt === vehicleType ? "text-white/70" : "text-neutral-500"}`}>
                  {VEHICLE_LABEL[vt] || vt}
                </div>
                <div className="font-display font-bold text-xl mt-0.5">{grid[vt]} km</div>
                {vt === vehicleType && <div className="text-[10px] uppercase tracking-wide mt-0.5 text-white/80">Votre gabarit</div>}
              </div>
            ))}
          </div>
          <div className="text-xs text-neutral-500 mt-2">
            Plus votre véhicule est grand, plus le périmètre d'alerte est large — les grands gabarits acceptent des trajets plus longs.
          </div>
        </div>
      )}
    </div>
  );
}
